import type { MovieCategory } from '@/types';

interface FilterPanelProps {
  genres: string[];
  regions: string[];
  years: number[];
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  selectedTag: string;
  sort: string;
  onToggleGenre: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onChangeTag: (value: string) => void;
  onChangeSort: (value: string) => void;
  onReset: () => void;
  onClose?: () => void;
}

const tagOptions = [
  { value: 'all', label: '全部' },
  { value: 'hot', label: '热门' },
  { value: 'new', label: '一周新片' },
];

const sortOptions: { value: string; label: string }[] = [
  { value: 'latest', label: '最新上线' },
  { value: 'popular', label: '热度最高' },
  { value: 'rating', label: '评分最高' },
];

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap border ${
        active
          ? 'bg-primary-500 border-primary-500 text-background-50'
          : 'bg-background-100 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({
  genres,
  regions,
  years,
  selectedGenres,
  selectedRegions,
  selectedYears,
  selectedTag,
  sort,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onChangeTag,
  onChangeSort,
  onReset,
  onClose,
}: FilterPanelProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <h2 className="text-sm font-medium text-foreground-900">筛选与排序</h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer"
          >
            收起
          </button>
        )}
      </div>

      <div className="mt-3 md:mt-0 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
          <span className="sm:w-16 shrink-0 pt-1 text-xs md:text-sm text-foreground-500">类型</span>
          <div className="flex flex-wrap gap-2">
            {genres.map((genre) => (
              <Chip
                key={genre}
                label={genre}
                active={selectedGenres.includes(genre)}
                onClick={() => onToggleGenre(genre)}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
          <span className="sm:w-16 shrink-0 pt-1 text-xs md:text-sm text-foreground-500">地区</span>
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <Chip
                key={region}
                label={region}
                active={selectedRegions.includes(region)}
                onClick={() => onToggleRegion(region)}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
          <span className="sm:w-16 shrink-0 pt-1 text-xs md:text-sm text-foreground-500">年份</span>
          <div className="flex flex-wrap gap-2">
            {years.map((year) => (
              <Chip
                key={year}
                label={`${year}`}
                active={selectedYears.includes(year)}
                onClick={() => onToggleYear(year)}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
          <span className="sm:w-16 shrink-0 pt-1 text-xs md:text-sm text-foreground-500">精选</span>
          <div className="flex flex-wrap gap-2">
            {tagOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                active={selectedTag === option.value}
                onClick={() => onChangeTag(option.value)}
              />
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-foreground-500">排序</span>
            <div className="relative">
              <select
                value={sort}
                onChange={(event) => onChangeSort(event.target.value)}
                aria-label="排序方式"
                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-background-200 bg-background-100 text-xs md:text-sm text-foreground-800 outline-none focus:border-primary-300 cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i
                className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-500 pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-200 text-xs md:text-sm text-foreground-600 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer w-fit whitespace-nowrap"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}

export type { FilterPanelProps };
export type TypeFilter = MovieCategory | 'all';